import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from 'src/styles/layout.module.css';
import { Button, Input, Space, Tag, Typography } from 'antd';
import { CloseOutlined, EditOutlined, PlusOutlined, ProfileTwoTone, SearchOutlined } from '@ant-design/icons';
import { AppDispatch } from 'src/index';
import { useDispatch } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import tileStyles from 'src/styles/tile.module.css';
import { Link, useParams } from 'react-router-dom';
import { useDebounce } from 'react-use';
import ConfirmButton from 'src/components/atoms/ConfirmButton';
import LoaderWrapper from 'src/components/atoms/LoaderWrapper';
import { fetchCompanies } from 'src/features/companies/companies.state';
import { useCompaniesValues } from 'src/features/companies/companies.hooks';
import {
  fetchTemplateOfflineCompaniesAPI,
  deleteTemplateFromCompanyAPI,
  assignTemplateOfflineToCompanyAPI,
} from 'src/features/templates/templates.api';

interface OfflineCompanyItem {
  id: string;
  name: string;
}

interface TemplateAddOfflineCompaniesProps {
  templateId: string;
  currentCompanies: OfflineCompanyItem[];
  onCompanyAdded: (companyId: string) => void;
}

const TemplateAddOfflineCompanies: React.FC<TemplateAddOfflineCompaniesProps> = props => {
  const intl = useIntl();
  const dispatch: AppDispatch = useDispatch();
  const companies = useCompaniesValues();
  const [name, setName] = useState<string | undefined>(undefined);
  const currentCompanyIds = useMemo(() => props.currentCompanies.map(c => c.id), [props.currentCompanies]);
  const [excludedCompanyId, setExcludedCompanyId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleAddButtonClick = useCallback(
    async (companyId: string) => {
      setIsLoading(true);
      try {
        await assignTemplateOfflineToCompanyAPI({
          templateId: props.templateId,
          companyId,
        });
        setExcludedCompanyId(companyId);
        setTimeout(() => setExcludedCompanyId(null), 100);
        props.onCompanyAdded(companyId);
      } catch (error) {
        console.error('Error assigning template to company:', error);
      }
      setIsLoading(false);
    },
    [props.templateId, props.onCompanyAdded]
  );

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      await dispatch(fetchCompanies({}));
      setIsLoading(false);
    })();
  }, [dispatch]);

  useDebounce(
    () => {
      if (name !== undefined && !isLoading) {
        (async () => {
          setIsLoading(true);
          await dispatch(fetchCompanies({ name: name }));
          setIsLoading(false);
        })();
      }
    },
    300,
    [dispatch, name]
  );

  const availableCompanies = companies.filter(
    c => !currentCompanyIds.includes(c.id) && !c.disabled && c.id !== excludedCompanyId
  );

  return (
    <>
      <Input
        autoFocus
        allowClear
        placeholder={intl.formatMessage({ id: 'placeholder.SearchVideo' })}
        prefix={<SearchOutlined />}
        onChange={e => {
          setName(e.target.value);
        }}
        style={{ marginBottom: 10 }}
      />
      <div className={styles.content}>
        <LoaderWrapper loading={isLoading}>
          {availableCompanies.map(c => (
            <div key={c.id} className={tileStyles.listTile}>
              <Link to={`/commercial/companies/${c.id}`} className={tileStyles.tileName}>
                <Space>
                  <ProfileTwoTone />
                  {c.name}
                </Space>
              </Link>
              <Space>
                {c.disabled && (
                  <Tag color={'orange'}>
                    <FormattedMessage id={'text.archived'} />
                  </Tag>
                )}
                <Button type={'primary'} size='small' onClick={() => handleAddButtonClick(c.id)}>
                  <FormattedMessage id={'action.Add'} />
                </Button>
              </Space>
            </div>
          ))}
        </LoaderWrapper>
      </div>
    </>
  );
};

const ContentTemplateOfflineCompaniesTab: React.FC = () => {
  const { templateId } = useParams() as { templateId: string };
  const [addMode, setAddMode] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [data, setData] = useState<OfflineCompanyItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetchTemplateOfflineCompaniesAPI({ templateId });
      setData(response.data.items || []);
    } catch (error) {
      console.error('Error fetching offline companies:', error);
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, [templateId]);

  useEffect(() => {
    fetchData();
  }, [fetchData, addMode]);

  const handleRemoveButtonClick = useCallback(
    async (companyId: string) => {
      setIsLoading(true);
      try {
        await deleteTemplateFromCompanyAPI({
          templateId,
          companyId,
        });
        setData(prev => prev.filter(item => item.id !== companyId));
      } catch (error) {
        console.error('Error removing template from company:', error);
      }
      setIsLoading(false);
    },
    [templateId]
  );

  const handleAddButtonClick = useCallback(() => {
    setAddMode(m => !m);
  }, []);

  const handleEditButtonClick = useCallback(() => {
    setEditMode(m => !m);
  }, []);

  const handleCompanyAdded = useCallback(
    (companyId: string) => {
      fetchData();
    },
    [fetchData]
  );

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Typography.Title level={4}>
          <FormattedMessage id={'view.content.template.tab.companies.title.offline'} />
        </Typography.Title>
        <Space direction={'horizontal'}>
          {!editMode && (
            <Button type={addMode ? undefined : 'primary'} onClick={handleAddButtonClick}>
              {addMode ? <CloseOutlined /> : <PlusOutlined />}
            </Button>
          )}
          {!addMode && (
            <Button onClick={handleEditButtonClick}>{editMode ? <CloseOutlined /> : <EditOutlined />}</Button>
          )}
        </Space>
      </div>
      <div className={styles.content}>
        {addMode && (
          <TemplateAddOfflineCompanies
            templateId={templateId}
            currentCompanies={data}
            onCompanyAdded={handleCompanyAdded}
          />
        )}
        {!addMode && (
          <LoaderWrapper loading={isLoading}>
            {data?.map(item => (
              <div key={item.id} className={tileStyles.listTile}>
                <Link to={`/commercial/companies/${item.id}`} className={tileStyles.tileName}>
                  <Space>
                    <ProfileTwoTone />
                    {item.name}
                  </Space>
                </Link>
                <Space>
                  {editMode && (
                    <ConfirmButton danger size='small' onClick={() => handleRemoveButtonClick(item.id)}>
                      <FormattedMessage id={'action.Remove'} />
                    </ConfirmButton>
                  )}
                </Space>
              </div>
            ))}
          </LoaderWrapper>
        )}
      </div>
    </div>
  );
};

export default ContentTemplateOfflineCompaniesTab;
